import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "./App.css";
import HomePage from "./pages/Home";
import ShopPage from "./pages/Shop";
import TrainingEnquiryPage from "./pages/TrainingEnquiry";
import CheckoutPage from "./pages/Checkout";
import PaymentSuccessPage from "./pages/PaymentSuccess";
import PaymentCancelPage from "./pages/PaymentCancel";
import AdminLoginPage from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import ReviewsPage from "./pages/Reviews";
import GalleryPage from "./pages/Gallery";
import TrainingPage from "./pages/Training";
import { LandingLayout } from "./components/LandingLayout";
import { AuthProvider } from "./context/AuthContext";
import { useSiteContent } from "./hooks/useSiteContent";
import { Toaster } from "./components/ui/sonner";

function BookingsPage() {
  const content = useSiteContent();
  const bookingsImage =
    content?.bookings_image ||
    "https://customer-assets.emergentagent.com/job_beauty-lashes-dupe/artifacts/j0uljqja_IMG_3060.jpeg";
  return (
    <div className="pb-12">
      <header className="mb-6 space-y-2">
        <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
          Bookings
        </p>
        <h1 className="text-2xl font-medium tracking-tight text-neutral-900">
          Book Your Appointment
        </h1>
        <p className="max-w-xl text-sm text-neutral-600">
          Book lash and lip blush appointments directly through your official
          Fresha booking link for Lash Dollz Vk Beauty.
        </p>
      </header>

      <section className="grid gap-8 md:grid-cols-[1.1fr_0.9fr] md:items-start">
        <div className="space-y-4">
          <a
            href="https://www.fresha.com/book-now/dollz-haus-jdumfios/services?lid=2927201&share=true&pId=2829974"
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-10 items-center justify-center rounded-full bg-black px-10 text-[11px] font-medium uppercase tracking-[0.25em] text-white transition-colors duration-150 hover:bg-neutral-800"
          >
            Go to booking app
          </a>
          <p className="text-xs text-neutral-500">
            All bookings are managed securely through Fresha. Lash products can
            be purchased directly in our{" "}
            <a href="/shop" className="underline underline-offset-4 hover:text-neutral-900">online shop</a>.
          </p>
        </div>

        <article className="overflow-hidden rounded-3xl border border-neutral-200 bg-white">
          <div className="relative">
            <img
              src="https://customer-assets.emergentagent.com/job_beauty-lashes-dupe/artifacts/j0uljqja_IMG_3060.jpeg"
              alt="Lip blush result at Lash Dollz Vk Beauty"
              className="h-64 w-full object-cover md:h-72"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-5 pb-4 pt-10 text-xs text-white">
              <p className="text-[11px] uppercase tracking-[0.25em]">
                Permanent Makeup
              </p>
              <p className="mt-1 text-sm font-medium">
                Lip blush & lash appointments in one place
              </p>
            </div>
          </div>
          <div className="px-5 py-4 text-xs text-neutral-600">
            <p>
              Choose from fluffy lash extensions, lip blush, or combo
              appointments on the next step. Fresha will show live availability
              for Lash Dollz Vk Beauty.
            </p>
          </div>
        </article>
      </section>
    </div>
  );
}

function AboutPage() {
  const content = useSiteContent();
  const aboutImage =
    content?.about_image ||
    "https://customer-assets.emergentagent.com/job_beauty-lashes-dupe/artifacts/jfd7fi8g_IMG_6390.jpeg";
  return (
    <div className="pb-12">
      <header className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
            About Lash Dollz Vk Beauty & Academy
          </p>
          <h1 className="text-2xl font-medium tracking-tight text-neutral-900 md:text-3xl">
            Lash artistry, permanent makeup & education in Tralee, Co. Kerry
          </h1>
        </div>
        <div className="overflow-hidden rounded-3xl border border-neutral-200 bg-white md:w-64">
          <img
            src={aboutImage}
            alt="Veronika Klisina receiving an award at a beauty industry event"
            className="h-40 w-full object-cover md:h-48"
          />
        </div>
      </header>

      <div className="space-y-4 text-sm leading-relaxed text-neutral-700 max-w-3xl">
        <p>
          Welcome to Lash Dollz Beauty, based in Tralee, County Kerry, where
          luxury lash artistry, permanent makeup, and high-standard
          professional training come together.
        </p>
        <p>
          My name is <span className="font-semibold">Veronika Klisina</span>—the
          founder, artist, and educator behind Lash Dollz Beauty. Originally
          from Lithuania, I moved to Ireland nearly 20 years ago, and it&apos;s here
          that I built not only my career, but my passion, reputation, and
          award-winning beauty brand.
        </p>
        <p>
          With over five years of experience specialising in lash artistry
          including advanced UV lash application—and extensive qualifications
          in permanent makeup, including lip blush and powder brows, I pride
          myself on creating customised, natural-enhancing results for every
          client. My work is built on precision, ongoing education, and a deep
          love for the craft.
        </p>
        <p>
          Alongside client services, Lash Dollz Beauty has grown into an
          education hub, offering in-person beginner and advanced lash training
          courses, brow trainings, and ongoing mentorship for aspiring and
          established artists. Every training is designed to empower, build
          confidence, and create career success—not just certificates.
        </p>
        <p>
          Over the years, the hard work and dedication have been recognised. In
          2023, Lash Dollz Beauty was awarded <span className="font-semibold">Best
          Start-Up Business</span>, acknowledging growth, innovation, and
          excellence. In 2025, I was honoured to receive <span className="font-semibold">Outstanding
          Knowledge in My Chosen Field</span>, celebrating continuous learning
          and mastery in the beauty industry.
        </p>
        <p>
          Whether you&apos;re here for a treatment, training, or mentorship—you&apos;ll
          receive the highest level of professionalism, support, and care.
        </p>
        <p className="text-neutral-800">
          <span className="font-semibold">
            Enhancing confidence. Building artists. Setting standards.
          </span>
        </p>
      </div>
    </div>
  );
}

function FAQPage() {
  return (
    <div className="pb-12">
      <header className="mb-4 space-y-2">
        <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
          FAQ & Policies
        </p>
        <h1 className="text-2xl font-medium tracking-tight text-neutral-900">
          Studio Policies & Reminders
        </h1>
      </header>
      <p className="text-sm text-neutral-600">
        Please read the following studio policies carefully before your
        appointment. These are based on your current policy graphic and can be
        updated any time.
      </p>

      <div className="mt-6 grid gap-8 md:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] md:items-start">
        <section className="space-y-4 text-sm text-neutral-700">
          <div>
            <h2 className="text-xs font-medium uppercase tracking-[0.25em] text-neutral-500">
              Booking & refills
            </h2>
            <ul className="mt-3 space-y-2 list-disc pl-5">
              <li>
                When booking for refills, please make sure you have more than
                40% of lashes left. Anything less than 40% will be charged as a
                full set.
              </li>
              <li>
                A deposit is required to secure your booking. Deposits are not
                refundable, but can be moved to a different appointment time if
                you give more than 24 hours notice.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xs font-medium uppercase tracking-[0.25em] text-neutral-500">
              Before your appointment
            </h2>
            <ul className="mt-3 space-y-2 list-disc pl-5">
              <li>
                Please remove all makeup prior to coming to your appointment.
                Arriving with dirty lashes may result in lash bath charges.
              </li>
              <li>
                Please arrive without any eye makeup and do not curl your lashes
                on the day of your appointment.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xs font-medium uppercase tracking-[0.25em] text-neutral-500">
              Timing & guests
            </h2>
            <ul className="mt-3 space-y-2 list-disc pl-5">
              <li>
                Arriving late will cut into your appointment time and may
                result in additional charges if your appointment has to be
                cancelled.
              </li>
              <li>
                For purposes of safety and sanitation, we cannot allow more
                than one guest per room. No children allowed.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xs font-medium uppercase tracking-[0.25em] text-neutral-500">
              Cancellations & no-shows
            </h2>
            <ul className="mt-3 space-y-2 list-disc pl-5">
              <li>
                A 50% booking cancellation fee will be charged for cancellation
                and rescheduling less than 24 hours before your appointment.
              </li>
              <li>
                No-shows will result in 100% of the booking fee of the service
                booked. No-shows for a second time will no longer be able to
                book with Lash Dollz Vk Beauty.
              </li>
            </ul>
          </div>
        </section>

        <aside className="hidden overflow-hidden rounded-3xl border border-neutral-200 bg-white md:block">
          <img
            src="https://customer-assets.emergentagent.com/job_beauty-lashes-dupe/artifacts/hzt8ly5y_Policies%20and%20Reminder%20Instagram%20Post.png"
            alt="Lash Dollz Vk Beauty policies and reminders graphic"
            className="h-full w-full object-cover"
          />
        </aside>
      </div>
    </div>
  );
}

function ContactPage() {
  return (
    <div className="pb-12">
      <header className="mb-4 space-y-2">
        <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
          Contact
        </p>
        <h1 className="text-2xl font-medium tracking-tight text-neutral-900">
          Get in touch
        </h1>
      </header>

      <div className="mb-8 grid gap-6 rounded-3xl border border-neutral-200 bg-white p-6 sm:grid-cols-2" data-testid="contact-details">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.25em] text-neutral-500">
            Studio
          </p>
          <p className="mt-2 text-sm font-medium text-neutral-900">
            Lash Dollz Vk Beauty · Dollz Haus
          </p>
          <p className="mt-1 text-sm leading-relaxed text-neutral-600">
            Milk Market Lane
            <br />
            Tralee, County Kerry
            <br />
            V92 F3XY, Ireland
          </p>
          <a
            href="https://www.google.com/maps/search/?api=1&query=Milk+Market+Lane,+Tralee,+County+Kerry,+V92+F3XY,+Ireland"
            target="_blank"
            rel="noreferrer"
            data-testid="contact-directions"
            className="mt-3 inline-flex h-9 items-center justify-center rounded-full border border-neutral-900 px-5 text-[11px] font-medium uppercase tracking-[0.2em] text-neutral-900 transition-colors hover:bg-neutral-900 hover:text-white"
          >
            Get directions
          </a>
        </div>
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.25em] text-neutral-500">
            Email
          </p>
          <a
            href="mailto:lashdollzvk@gmail.com"
            className="mt-2 block text-sm text-neutral-900 hover:underline"
          >
            lashdollzvk@gmail.com
          </a>
          <p className="mt-4 text-[11px] font-medium uppercase tracking-[0.25em] text-neutral-500">
            Bookings
          </p>
          <a href="/bookings" className="mt-1 block text-sm text-neutral-900 hover:underline">
            Book your appointment on Fresha
          </a>
        </div>
      </div>
      <form className="mt-4 max-w-md space-y-4 text-sm">
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-[0.2em] text-neutral-700">
            Name
          </label>
          <input
            type="text"
            className="h-10 w-full rounded-full border border-neutral-300 bg-white px-4 text-sm outline-none transition-colors duration-150 focus:border-neutral-900"
            placeholder="Your name"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-[0.2em] text-neutral-700">
            Email
          </label>
          <input
            type="email"
            className="h-10 w-full rounded-full border border-neutral-300 bg-white px-4 text-sm outline-none transition-colors duration-150 focus:border-neutral-900"
            placeholder="you@example.com"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-[0.2em] text-neutral-700">
            Message
          </label>
          <textarea
            className="min-h-[120px] w-full rounded-3xl border border-neutral-300 bg-white px-4 py-3 text-sm outline-none transition-colors duration-150 focus:border-neutral-900"
            placeholder="Tell us about your lash goals or questions"
          />
        </div>
        <button
          type="button"
          onClick={() =>
            alert(
              "This contact form is mocked in the clone. In production, it would send to your email or CRM.",
            )
          }
          className="inline-flex h-10 items-center justify-center rounded-full bg-black px-10 text-[11px] font-medium uppercase tracking-[0.25em] text-white transition-colors duration-150 hover:bg-neutral-800"
        >
          Send message
        </button>
      </form>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" />
        <Routes>
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route
            path="/*"
            element={
              <LandingLayout>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/shop" element={<ShopPage />} />
                  <Route path="/training" element={<TrainingPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/payment/success" element={<PaymentSuccessPage />} />
                  <Route path="/payment/cancel" element={<PaymentCancelPage />} />
                  <Route path="/training-enquiry" element={<TrainingEnquiryPage />} />
                  <Route path="/bookings" element={<BookingsPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/reviews" element={<ReviewsPage />} />
                  <Route path="/gallery" element={<GalleryPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                </Routes>
              </LandingLayout>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
